import { isValidCardCode, parseCardCode } from 'lib/card-utils';

type HandCategory =
  | 'trail'
  | 'pure_sequence'
  | 'sequence'
  | 'color'
  | 'pair'
  | 'high_card';

const RANK_VALUE: Record<string, number> = {
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
  '9': 9,
  '10': 10,
  J: 11,
  Q: 12,
  K: 13,
  A: 14,
};

const CATEGORY_LABEL: Record<HandCategory, string> = {
  trail: 'Trail',
  pure_sequence: 'Pure Sequence',
  sequence: 'Sequence',
  color: 'Color',
  pair: 'Pair',
  high_card: 'High Card',
};

function rankValue(rank: string): number {
  return RANK_VALUE[rank] ?? 0;
}

function isFlush(suits: string[]): boolean {
  return suits.every((s) => s === suits[0]);
}

function isStraight(ranks: number[]): boolean {
  const sorted = [...ranks].sort((a, b) => a - b);
  const normal = sorted[2] - sorted[1] === 1 && sorted[1] - sorted[0] === 1;
  const aceLow = sorted[0] === 2 && sorted[1] === 3 && sorted[2] === 14;
  return normal || aceLow;
}

function evaluateClassic(codes: string[]): HandCategory | null {
  const cards = codes
    .map((code) => {
      if (!isValidCardCode(code)) return null;
      const parsed = parseCardCode(code);
      return {
        rank: rankValue(parsed.rank),
        suit: parsed.suitKey,
      };
    })
    .filter((c): c is { rank: number; suit: string } => c != null);

  if (cards.length !== 3) return null;

  const ranks = cards.map((c) => c.rank).sort((a, b) => b - a);
  const suits = cards.map((c) => c.suit);
  const flush = isFlush(suits);
  const straight = isStraight(ranks);

  const counts = new Map<number, number>();
  for (const rank of ranks) counts.set(rank, (counts.get(rank) ?? 0) + 1);
  const entries = [...counts.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0]);

  if (entries[0][1] === 3) return 'trail';
  if (straight && flush) return 'pure_sequence';
  if (straight) return 'sequence';
  if (flush) return 'color';
  if (entries[0][1] === 2) return 'pair';
  return 'high_card';
}

/** Teen Patti table labels — drop poker aliases from older server payloads. */
export function displayHandCategory(label?: string | null): string | undefined {
  if (label == null) return undefined;
  const trimmed = String(label).trim();
  if (!trimmed) return undefined;
  if (/^trail\b/i.test(trimmed)) return 'Trail';
  if (/^color\b/i.test(trimmed)) return 'Color';
  return trimmed;
}

/** Octro-style hand rank label from three card codes (classic Teen Patti). */
export function handCategoryLabel(codes: string[]): string | null {
  const category = evaluateClassic(codes);
  return category ? CATEGORY_LABEL[category] : null;
}

export function faceUpCardCodes(
  cards: { code: string; hidden?: boolean }[] | undefined
): string[] {
  if (!cards?.length) return [];
  return cards
    .filter((c) => c.code && c.code !== 'back' && !c.hidden && isValidCardCode(c.code))
    .map((c) => c.code.trim().toUpperCase());
}
